import dayjs from 'dayjs';
import React from 'react';
import { Link } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  AgendaTimelineItem,
  ComputedAppointmentStatusLabel,
} from '@eluve/blocks';
import { Stack, Text } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useUserName } from '@eluve/smart-blocks';

import { useShowAppointmentsOwner } from '../../../settings/hooks/useShowAppointmentsOwner';

interface AppointmentAgendaTimelineItemProps {
  appointmentId: string;
}

export const appointmentTimelineInfoFragment = graphql(`
  fragment AppointmentInfo on Appointments @_unmask {
    __typename
    id
    startDate
    endDate
    name
    status
    userId
    patient {
      __typename
      id
      firstName
      lastName
    }
    statusLabel
    session_type {
      __typename
      id
      name
    }
  }
`);

const AppointmentNameAndType: React.FC<{
  name: string | undefined;
  sessionType: string | null | undefined;
}> = ({ name, sessionType = '' }) => {
  return (
    <Stack fullWidth={false} gap={0.5}>
      {Boolean(sessionType && sessionType !== name) && (
        <Text className="font-light">{sessionType}</Text>
      )}
      <Text color="secondary" className="font-light">
        {name ?? ''}
      </Text>
    </Stack>
  );
};

const AppointmentOwner: React.FC<{ userId: string }> = ({ userId }) => {
  const userName = useUserName(userId);
  return (
    <Text inline size="xs">
      {userName}
    </Text>
  );
};

export const AppointmentAgendaTimelineItem: React.FC<
  AppointmentAgendaTimelineItemProps
> = ({ appointmentId }) => {
  const { t } = useTranslation();
  const appointment = useCompleteFragment({
    fragment: appointmentTimelineInfoFragment,
    key: {
      id: appointmentId,
    },
  });
  const [showAppointmentsOwner] = useShowAppointmentsOwner();

  const startTime = new Date(appointment.startDate);
  const endTime = appointment.endDate
    ? new Date(appointment.endDate)
    : dayjs(startTime).add(1, 'hour').toDate();

  const patient = appointment.patient;
  return (
    <AgendaTimelineItem startTime={startTime} endTime={endTime} asChild>
      <Link
        to={`../appointments/${appointmentId}`}
        className="min-w-[240px] gap-2"
      >
        <Stack fullWidth={false} gap={2}>
          <Stack
            className="flex-wrap-reverse"
            align="stretch"
            direction="horizontal"
            justify="between"
            gap={2}
          >
            <Text
              inline
              color="secondary"
              weight="semibold"
              className="privacy-text"
            >
              {patient
                ? `${patient.firstName} ${patient.lastName}`
                : t('home.agenda.patientNotSpecified')}
            </Text>
            <Text color="secondary" className="min-w-min font-light">
              {dayjs(startTime).format('h:mm a')} -{' '}
              {dayjs(endTime).format('h:mm a')}
            </Text>
          </Stack>
          <Stack
            align="start"
            direction="horizontal"
            justify="between"
            wrap
            gap={1.5}
          >
            <AppointmentNameAndType
              name={appointment.name}
              sessionType={appointment.session_type?.name}
            />
            <Stack
              className="min-w-min"
              direction="horizontal"
              fullWidth={false}
              gap={2}
              wrap
            >
              <ComputedAppointmentStatusLabel
                statusLabel={appointment.statusLabel ?? ''}
              />
            </Stack>
          </Stack>
          {showAppointmentsOwner && (
            <AppointmentOwner userId={appointment.userId} />
          )}
        </Stack>
      </Link>
    </AgendaTimelineItem>
  );
};
